/**
 * PWA wake-up check ("Aufwach-Prüfung").
 *
 * Problem: an installed PWA that sat in the background for hours/days can
 * come back frozen — expired JWT (silent 401s), a new deploy that removed
 * the old hashed chunks, or dead in-flight connections.
 *
 * Solution: when the document becomes visible again after being hidden for
 * longer than RESUME_HIDDEN_THRESHOLD_MS we run a quick health/session check
 * with a short timeout:
 *   - 401            → clear auth state and go to the login page.
 *   - timeout/error  → browser announces a refresh, no automatic hard reload.
 *   - session ok     → compare the deployed frontend bundle hash against the
 *                      one currently in memory; if a new version was deployed
 *                      while we slept → announce an update for explicit click.
 *
 * The core logic is dependency-injected so it can be unit-tested in node.
 */

export const RESUME_HIDDEN_THRESHOLD_MS = 5 * 60_000;
export const RESUME_CHECK_TIMEOUT_MS = 5_000;
/** sessionStorage key guarding against reload loops. */
export const RESUME_RELOAD_GUARD_KEY = "alta_resume_reload";
/** Minimum ms between automatic resume-triggered reloads. */
export const RESUME_RELOAD_GUARD_MS = 30_000;

export type ResumeCheckResult =
  | "ok" // session valid, version current — nothing to do
  | "unauthorized" // 401 → auth cleared, redirected to login
  | "reloaded" // hard reload triggered (dead connection or new version)
  | "reload-suppressed" // reload wanted but guard blocked a loop
  | "update-available" // browser defers reload until the user explicitly clicks
  | "skipped"; // nothing to check (e.g. no fetch available)

export interface ResumeCheckDeps {
  fetchFn: typeof fetch;
  getToken(): string | null;
  clearAuth(): void;
  redirectToLogin(): void;
  reload(): void;
  onUpdateAvailable?(): void;
  /** Timestamp (ms) of the last resume-triggered reload, 0 if none. */
  getReloadGuard(): number;
  setReloadGuard(ts: number): void;
  now(): number;
  /** The module script src of the currently running bundle (null in dev). */
  currentScriptSrc(): string | null;
  timeoutMs?: number;
}

async function fetchWithTimeout(
  fetchFn: typeof fetch,
  url: string,
  timeoutMs: number,
  init?: RequestInit,
): Promise<Response> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetchFn(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
}

/** Extract the hashed main-bundle script src from an index.html document. */
export function extractMainScriptSrc(html: string): string | null {
  const m = html.match(/<script[^>]*type="module"[^>]*src="([^"]+)"/i)
    ?? html.match(/<script[^>]*src="([^"]+)"[^>]*type="module"/i);
  return m ? m[1] : null;
}

function guardedReload(deps: ResumeCheckDeps): ResumeCheckResult {
  const now = deps.now();
  if (now - deps.getReloadGuard() < RESUME_RELOAD_GUARD_MS) {
    return "reload-suppressed";
  }
  deps.setReloadGuard(now);
  if (deps.onUpdateAvailable) {
    deps.onUpdateAvailable();
    return "update-available";
  }
  deps.reload();
  return "reloaded";
}

/**
 * Run the wake-up check. Called when the app becomes visible again after
 * a long background period.
 */
export async function runResumeCheck(deps: ResumeCheckDeps): Promise<ResumeCheckResult> {
  const timeoutMs = deps.timeoutMs ?? RESUME_CHECK_TIMEOUT_MS;
  const token = deps.getToken();

  // 1) Health/session check with a short timeout so a dead connection can
  //    never leave the UI hanging.
  try {
    if (token) {
      const res = await fetchWithTimeout(deps.fetchFn, "/auth/me", timeoutMs, {
        // The browser sends the HttpOnly session cookie automatically.
        credentials: "same-origin",
        cache: "no-store",
      });
      if (res.status === 401) {
        deps.clearAuth();
        deps.redirectToLogin();
        return "unauthorized";
      }
      if (!res.ok) {
        // Backend up but unhealthy (5xx) → a reload gives the user either a
        // working app or the maintenance screen instead of a frozen UI.
        return guardedReload(deps);
      }
    } else {
      const res = await fetchWithTimeout(deps.fetchFn, "/health", timeoutMs, {
        cache: "no-store",
      });
      if (!res.ok) return guardedReload(deps);
    }
  } catch {
    // Timeout or network error → the connection from before the freeze is
    // dead. One guarded hard reload restores a clean state.
    return guardedReload(deps);
  }

  // 2) Version check: was a new frontend deployed while we were sleeping?
  //    Old index.html in memory references deleted hash-chunks → lazy route
  //    navigation would hang. Compare bundle hashes and reload if changed.
  const currentSrc = deps.currentScriptSrc();
  if (currentSrc) {
    try {
      const res = await fetchWithTimeout(deps.fetchFn, "/index.html", timeoutMs, {
        cache: "no-store",
      });
      if (res.ok) {
        const html = await res.text();
        const freshSrc = extractMainScriptSrc(html);
        if (freshSrc && freshSrc !== currentSrc) {
          return guardedReload(deps);
        }
      }
    } catch {
      // Network worked a moment ago; ignore version-check failures.
    }
  }

  return "ok";
}

/** Build browser deps (extracted for reuse; not used in unit tests). */
export function browserDeps(): ResumeCheckDeps {
  return {
    fetchFn: (...args) => window.fetch(...args),
    // The readable CSRF cookie acts only as a presence marker; the session
    // JWT itself is HttpOnly and is sent by fetch automatically.
    getToken: () => (
      document.cookie.split("; ").some((entry) => entry.startsWith("alta_csrf="))
        ? "cookie-session"
        : null
    ),
    clearAuth: () => {
      document.cookie = "alta_csrf=; Max-Age=0; Path=/; SameSite=Lax";
    },
    redirectToLogin: () => {
      window.location.href = "/login";
    },
    reload: () => window.location.reload(),
    // Resuming a tab must not replace an already visible dashboard. Session
    // denials still clear authentication/redirect above; only refresh is deferred.
    onUpdateAvailable: () => window.dispatchEvent(new Event("alta-app-update-available")),
    getReloadGuard: () => Number(sessionStorage.getItem(RESUME_RELOAD_GUARD_KEY) ?? "0"),
    setReloadGuard: (ts) => sessionStorage.setItem(RESUME_RELOAD_GUARD_KEY, String(ts)),
    now: () => Date.now(),
    currentScriptSrc: () => {
      const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
      return el?.getAttribute("src") ?? null;
    },
  };
}

/**
 * Install the visibilitychange listener. Returns an uninstall function.
 * Exposed with an injectable runner for testability.
 */
export function installResumeCheck(
  runner: (deps: ResumeCheckDeps) => Promise<ResumeCheckResult> = runResumeCheck,
): () => void {
  let hiddenAt: number | null = null;
  let running = false;

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      return;
    }
    // became visible again
    const wasHiddenFor = hiddenAt !== null ? Date.now() - hiddenAt : 0;
    hiddenAt = null;
    if (wasHiddenFor < RESUME_HIDDEN_THRESHOLD_MS || running) return;
    running = true;
    runner(browserDeps())
      .catch(() => undefined)
      .finally(() => {
        running = false;
      });
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  return () => document.removeEventListener("visibilitychange", onVisibilityChange);
}
